import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import analytics from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage if available
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('utentik_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const authChecked = useRef(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // If already checked and we have a user, don't check again
    if (authChecked.current && user) {
      setLoading(false);
      return;
    }

    try {
      const storedToken = localStorage.getItem('access_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        withCredentials: true
      });
      setUser(response.data);
      setToken(storedToken);
      // Save user to localStorage for persistence
      localStorage.setItem('utentik_user', JSON.stringify(response.data));
      authChecked.current = true;
    } catch (error) {
      console.debug('Auth check:', error.response?.status || error.message);
      // Only clear if we get a 401, not for network errors
      if (error.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('utentik_user');
        setUser(null);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/api/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    const { user: userData, access_token } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('utentik_user', JSON.stringify(userData));
    setUser(userData);
    setToken(access_token);
    authChecked.current = true;
    analytics.login('email');
    analytics.identify(userData.user_id, { email: userData.email, role: userData.role, name: userData.name });
    return userData;
  };

  const register = async (email, password, name, role = 'user') => {
    const response = await axios.post(`${API}/api/auth/register`, 
      { email, password, name, role },
      { withCredentials: true }
    );
    const { user: userData, access_token } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('utentik_user', JSON.stringify(userData));
    setUser(userData);
    setToken(access_token);
    authChecked.current = true;
    analytics.register(role);
    analytics.identify(userData.user_id, { email: userData.email, role: userData.role, name: userData.name });
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId, role) => {
    const response = await axios.post(`${API}/api/auth/google/session`, 
      { session_id: sessionId, role: role || 'user' },
      { withCredentials: true }
    );
    const { user: userData, access_token } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('utentik_user', JSON.stringify(userData));
    setUser(userData);
    setToken(access_token);
    authChecked.current = true;
    analytics.login('google');
    analytics.identify(userData.user_id, { email: userData.email, role: userData.role, name: userData.name });
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error.message);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('utentik_user');
    setUser(null);
    setToken(null);
    authChecked.current = false;
    analytics.logout();
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('utentik_user', JSON.stringify(userData));
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
      const { access_token } = response.data;
      localStorage.setItem('access_token', access_token);
      setToken(access_token);
      return access_token;
    } catch (error) {
      await logout();
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    updateUser,
    refreshToken,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
